import styled from '@emotion/styled';
import {
  gradient,
  palette,
  spacing,
  radius,
  shadow,
  typography,
  layout,
  transition,
  duration,
  easing,
} from '../../style-utils/theme';
import { down, touch } from '../../style-utils/media-queries';
import { SCALE } from '../../motion/tokens';

/**
 * Styled layer for every shift surface: the clock control, the shift monitor,
 * and the on-duty block inside the handover.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THERE IS NO AURORA IN THIS FILE, AND THAT IS THE POINT.
 * ═══════════════════════════════════════════════════════════════════════════
 * design_system.md:351-357 reserves `gradient.aurora` EXCLUSIVELY for Lobbero
 * IQ — AI entry points, AI-generated content, the AI thinking state — because a
 * single reserved gradient is the only visual vocabulary the product has for
 * "a model wrote this". The shift clock is not AI: lobbero_be/src/CRUD/shifts.js
 * opens by saying so and by requiring every route to work with
 * ai_config.enabled = false. A clock-in button wearing the AI mark would tell
 * the desk that clocking on is an AI feature, which is false, and would spend
 * the reserved signal on a button that has nothing to do with it.
 *
 * So the primary action here is `gradient.primary` + `shadow.brand` — the
 * ordinary product CTA from design_system.md:284-288 — and the card is the plain
 * non-AI recipe already used by iq-ask-panel's Wrap and iq-raw-log's LogWrap:
 * surface + 1px line + radius.md + shadow.sm.
 */

/* ─────────────────────────────  SHELL  ───────────────────────────── */

export const ShiftPage = styled.div`
  /* vh first as the fallback, dvh second to override it — design_system.md
     section 8 rule 7. On min-height the vh value only over-reserves, so this is
     a background-fill correction rather than a hidden-controls one.
     NO BACKTICKS IN THIS COMMENT: it sits inside a styled template literal and
     one would silently terminate the string. */
  min-height: 100vh;
  min-height: 100dvh;
  background: ${palette.porcelain};
  padding: ${spacing.md} ${spacing.sm} ${spacing.xxl};
`;

export const ShiftInner = styled.div`
  max-width: 860px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

export const PageHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  flex-wrap: wrap;
`;

export const PageTitle = styled.h1`
  font-family: ${typography.display};
  font-size: ${typography.size.xl}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.ink};
  margin: 0;
`;

export const PageSub = styled.p`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
  margin: ${spacing.half} 0 0;
`;

/* ─────────────────────────────  CARD  ────────────────────────────── */

export const ShiftCard = styled.section`
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};
  padding: ${spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};

  ${down('md')} {
    padding: ${spacing.sm};
  }
`;

export const CardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${spacing.xs};
`;

export const CardTitle = styled.h2`
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  margin: 0;
`;

export const CardLead = styled.p`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.inkSoft};
  margin: 0;
`;

/* ── THE HANDOVER USED TO SIT ABOVE THE CLOCK-IN, ON THIS SCREEN ──
   `HandoffLead`, `HandoffLeadTitle`, `GateBody`, `GateAction`, `GateBanner` and
   `GateBannerTitle` all lived here and are gone (2026-09-04).

   The 2026-08-21 shape put the desk's pending handover above the clock-in so it
   could be read before taking over, and this file grew a scrolling document and
   a sticky action bar to keep the one press on screen underneath it. The owner
   reversed it: the gate is a clean fullscreen "You are not clocked in / Clock
   in", and the handover ARRIVES as a layer the moment somebody clocks in and
   claims it.

   Deleted rather than left behind. A styled export nothing renders is a shape
   the next person will reach for believing it is the house pattern, and this one
   would put a document back in front of the only button on the screen. */



export const CardNote = styled.p`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slateLight};
  margin: 0;
`;

/* The main-menu slot that used to wrap this card is gone with the grouped
 * menu — the clock now sits in the title column (main-menu.styled.tsx's
 * TitleAside), sized by that column rather than by the option tiles. */

/* ────────────────────────────  ACTIONS  ──────────────────────────── */

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
`;

// design_system.md §9 Buttons → Primary. gradient.primary, NOT aurora.
/**
 * `$big` — THE ONE PRESS THE SCREEN EXISTS FOR.
 *
 * Owner instruction, 2026-08-14: "make the clock in buttons huge."
 *
 * Off the clock, clocking in is not one option among several — it is the whole
 * screen (see ClockSlot in main-menu.styled.tsx: no grid, no tiles, nothing else
 * to press). A 44px pill sitting alone in a 560px card was sized like a control
 * competing for attention on a busy screen, on a screen with no competition.
 *
 * A VARIANT, NOT A NEW SIZE FOR EVERY PrimaryAction. The same component is the
 * handover press and the resolve press, where 44px beside body copy is right.
 * Growing all of them to make one of them findable is how a design system stops
 * meaning anything.
 *
 * Full width, because the card it sits in is already the narrow column: a
 * centred pill inside a centred card reads as two boxes, and the target a
 * gloved hand actually gets is the row, not the word.
 */
export const PrimaryAction = styled.button<{ $big?: boolean }>`
  -webkit-appearance: none;
  appearance: none;
  border: none;
  min-height: ${({ $big }) => ($big ? '72px' : layout.buttonHeight)};
  ${({ $big }) => ($big ? 'width: 100%;' : '')}
  padding: 0 ${({ $big }) => ($big ? spacing.lg : spacing.md)};
  border-radius: ${radius.sm};
  background: ${gradient.primary};
  color: ${palette.white};
  font-family: ${typography.ui};
  font-size: ${({ $big }) =>
    $big ? `${typography.size.lg}px` : `${typography.size.sm}px`};
  font-weight: ${({ $big }) =>
    $big ? typography.weight.bold : typography.weight.semibold};
  letter-spacing: ${({ $big }) => ($big ? typography.tracking.snug : 'normal')};
  box-shadow: ${shadow.brand};
  cursor: pointer;
  /* Tokens, not 240ms ease (the e2e scanner flags off-scale durations).
     Lift at the base tempo with the spring curve; press to SCALE.pressed at
     the instant tempo — the same grammar as AppButton. */
  transition: ${transition.colors},
    transform ${duration.base} ${easing.spring};

  &:hover:not(:disabled) {
    background: ${gradient.primaryHover};
    box-shadow: ${shadow.brandHover};
    transform: translateY(-1px);
  }
  &:active:not(:disabled) {
    transform: scale(${SCALE.pressed});
    transition-duration: ${duration.instant};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
  &:disabled {
    background: ${palette.lineStrong};
    color: ${palette.slateLight};
    box-shadow: none;
    cursor: default;
  }
`;

// Secondary — surface + line, hover to the brand tint. Used for "clock out",
// which is a normal end-of-shift act and not a destructive one.
export const SecondaryAction = styled.button`
  -webkit-appearance: none;
  appearance: none;
  min-height: ${layout.buttonHeight};
  padding: 0 ${spacing.md};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover:not(:disabled) {
    background: ${palette.irisTintSoft};
    border-color: ${palette.lagoon};
  }
  &:focus-visible {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const QuietAction = styled.button`
  -webkit-appearance: none;
  appearance: none;
  min-height: ${layout.compactControlHeight};
  /* Coarse pointer: keep the compact look, meet the 44px floor. A desk tablet
     is this product's primary device, so a 32px control here is the common
     case and not the edge one. NO BACKTICKS IN THIS COMMENT - it lives inside a
     styled template literal. */
  ${touch} {
    height: auto;
    min-height: ${layout.touchTarget};
  }

  padding: 0 ${spacing.sm};
  border-radius: ${radius.pill};
  background: transparent;
  border: 1px solid ${palette.line};
  color: ${palette.slate};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.medium};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover:not(:disabled) {
    border-color: ${palette.lineStrong};
    color: ${palette.inkSoft};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
  /* KEYBOARD GETS WHAT THE POINTER GETS. This control styled :hover and nothing
     for focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

`;

export const TimeField = styled.input`
  height: ${layout.fieldHeight};
  padding: 0 ${spacing.sm};
  border-radius: ${radius.xs};
  border: 1px solid ${palette.line};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  box-shadow: ${shadow.xs};

  &:hover {
    border-color: ${palette.lineStrong};
  }
  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

export const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
  color: ${palette.slate};
`;

/* A blocking state that the reader must resolve, inset so it reads as a panel
 * inside the card rather than as a second card. Warning tint, not alert: a
 * forgotten clock-out is an ordinary human oversight, and painting it red every
 * morning teaches the desk to ignore red. */
export const ResolvePanel = styled.div`
  background: ${palette.warningSoft};
  border: 1px solid ${palette.warning};
  border-radius: ${radius.sm};
  padding: ${spacing.sm};
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

/* ─────────────────────  THE RECIPIENT PICKER  ─────────────────────
 *
 * STILL NO AURORA, and it is worth saying why on this particular control.
 *
 * Naming a recipient is what CAUSES a summary to be written, so the temptation
 * to mark it as an AI affordance is real. It is wrong. Choosing a colleague is a
 * chain-of-custody act performed entirely in SQL — the server picks the eligible
 * people out of `users` and `access_scope`, and no model is consulted about who
 * may receive a shift. The aurora belongs on the SUMMARY the handover carries
 * (iq-handoff.styled.tsx draws exactly that line), not on the act of handing it.
 *
 * It is an inset panel rather than a modal on purpose. The clock card sits at
 * the top of the main menu and this is the second half of "clock out"; throwing
 * a dialog over the screen for a two-tap choice buys nothing and lands on the
 * modal-stacking problems the app has already paid for once. */
export const PickerPanel = styled.div`
  background: ${palette.surfaceMuted};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  padding: ${spacing.sm};
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

export const SearchField = styled.input`
  height: ${layout.fieldHeight};
  padding: 0 ${spacing.sm};
  border-radius: ${radius.xs};
  border: 1px solid ${palette.line};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  box-shadow: ${shadow.xs};

  &::placeholder {
    color: ${palette.slateLight};
  }
  &:hover {
    border-color: ${palette.lineStrong};
  }
  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

/* Capped and scrollable. A desk with forty staff must not push the clock-out
 * button off the bottom of a phone screen. */
export const CandidateList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  max-height: 240px;
  overflow-y: auto;
`;

export const CandidateRow = styled.button<{ $selected?: boolean }>`
  -webkit-appearance: none;
  appearance: none;
  text-align: left;
  width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing.half} ${spacing.xs};
  min-height: ${layout.touchTarget};
  padding: ${spacing.half} ${spacing.sm};
  border-radius: ${radius.xs};
  border: 1px solid ${p => (p.$selected ? palette.iris : palette.line)};
  background: ${p => (p.$selected ? palette.irisTint : palette.surface)};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover:not(:disabled) {
    background: ${palette.irisTintSoft};
    border-color: ${palette.lineStrong};
  }
  &:focus-visible {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

export const CandidateName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  color: ${palette.ink};
`;

export const CandidateMeta = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slate};
`;

/* A quiet, non-alarming statement that the desk has a handover waiting. It is
 * NOT a modal and NOT an alert tint: D-25 asks for the handover to be there on
 * arrival, not to block whatever the reader actually opened the app to do. */
export const InboundBanner = styled.div`
  background: ${palette.irisTintSoft};
  border: 1px solid ${palette.irisTint};
  border-left: 3px solid ${palette.iris};
  border-radius: ${radius.sm};
  padding: ${spacing.xs} ${spacing.sm};
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
`;

/* ── THE LAST HANDOVER ON THE GATE (owner instruction, 2026-09-11) ──
   Rendered inside InboundBanner above Clock in (handoff-gate-panel.tsx). Every
   text block is CLAMPED: this is a summary that must never push the one press
   off a phone screen, which is what the 2026-08-21 document did. -webkit-box +
   line-clamp is the form WebKit and Blink both honour. NO BACKTICKS IN THESE
   COMMENTS: they sit near styled template literals. */
export const LastHandoverHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${spacing.xs};
  min-width: 0;
`;

export const LastHandoverTitle = styled.h3`
  margin: 0;
  min-width: 0;
  overflow-wrap: anywhere;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

export const LastHandoverMeta = styled.p`
  margin: 0;
  min-width: 0;
  overflow-wrap: anywhere;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

export const LastHandoverLine = styled.p`
  margin: 0;
  min-width: 0;
  overflow-wrap: anywhere;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.inkSoft};
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`;

export const LastHandoverItems = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;

  > li {
    font-family: ${typography.ui};
    font-size: ${typography.size.xs}px;
    line-height: ${typography.leading.normal};
    color: ${palette.inkSoft};
    overflow-wrap: anywhere;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
  }

  > li b {
    font-weight: ${typography.weight.semibold};
    color: ${palette.ink};
    margin-right: ${spacing.half};
  }
`;

/* ─────────────────────────────  LISTS  ───────────────────────────── */

export const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
`;

export const GroupTitle = styled.h3`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  margin: 0;
  /* text-transform is NOT set: one group's heading is a sentence the backend
   * dictates word for word, and upper-casing it would silently restate it. */
`;

export const PersonRow = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: ${spacing.xs};
  padding: ${spacing.xs} 0;
  border-top: 1px solid ${palette.line};

  &:first-of-type {
    border-top: none;
  }
`;

export const PersonName = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

export const PersonMeta = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};
`;

export const Mono = styled.span`
  font-family: ${typography.mono};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slate};
`;

export const EmptyLine = styled.p`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
  margin: 0;
  padding: ${spacing.xs} 0;
`;

/* The caveat that must travel with the data it qualifies. Neutral, because it
 * is a scoping note and not an alarm. */
export const Caveat = styled.p`
  margin: 0;
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.xs};
  background: ${palette.surfaceMuted};
  color: ${palette.inkSoft};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  line-height: ${typography.leading.normal};
`;
